import {
  StyledCreateEditInfrastructureDrawer,
  StyledCreateEditInfrastructureForm,
} from './style';
import {
  Button,
  Checkbox,
  Icon,
  Input,
  Spinner,
  TextArea,
} from '../../../../../ui';
import { useIntl } from 'react-intl';
import useQueryApiClient from '../../../../../utils/useQueryApiClient';
import { message } from 'antd';
import { useState } from 'react';
import { WorkingHours } from 'ui/WorkingHours';
import { useForm } from 'antd/es/form/Form';
import { InfrastructureProps } from 'interfaces/BackendModels';
import { convertISOToDayJs, convertToDayJs } from 'utils/timeHelpers';
import { options } from 'config/config';
import { useNavigate } from 'react-router-dom';
import { AdresSearch, AMKResponse } from 'ui/AdressSearch';

interface CreateEditProps {
  open: boolean;
  onClose: () => void;
  editInfrastructureId: number | null;
  setReloadCount: React.Dispatch<React.SetStateAction<number>>;
}

export const CreateEditInfrastructure = ({
  open,
  onClose,
  editInfrastructureId,
  setReloadCount,
}: CreateEditProps) => {
  const intl = useIntl();
  const [form] = useForm();
  const [isEditable, setIsEditable] = useState(true);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { appendData: storeInfrastructure } = useQueryApiClient({
    request: {
      url: '/api/v2/farm/infrastructures',
      method: 'POST',
    },
    onSuccess: () => handleSuccess(),
  });

  const { appendData: editInfrastructure } = useQueryApiClient({
    request: {
      url: `/api/v2/farm/infrastructures/:id?_method=PATCH`,
      method: 'PATCH',
    },
    onSuccess: () => handleSuccess(),
  });

  const { isLoading: isInfrastructureLoading } = useQueryApiClient({
    request: {
      url: `/api/v2/farm/infrastructures/${editInfrastructureId}`,
      method: 'GET',
      disableOnMount: editInfrastructureId === null,
    },
    onSuccess: (response: InfrastructureProps) => loadFormWithData(response),
  });

  const title = editInfrastructureId
    ? intl.formatMessage({ id: 'general.edit_infrastructure' })
    : intl.formatMessage({ id: 'general.add_new_infrastructure' });

  const submitText = editInfrastructureId
    ? intl.formatMessage({ id: 'general.edit' })
    : intl.formatMessage({ id: 'general.add' });

  const msg = editInfrastructureId
    ? intl.formatMessage({ id: 'message.success_edit_infrastructure' })
    : intl.formatMessage({ id: 'message.success_create_infrastructure' });

  const handleSubmit = async () => {
    try {
      await form.validateFields();

      const values = form.getFieldsValue();

      if (!values.show_on_map) {
        values.show_on_map = false;
      }

      if (!values.status) {
        values.status = false;
      }

      editInfrastructureId
        ? editInfrastructure(values, { id: values.id })
        : storeInfrastructure(values);
    } catch (e) {
      message.error(intl.formatMessage({ id: 'message.error' }));
    }
  };

  const loadFormWithData = (response: InfrastructureProps) => {
    setIsEditable(response.editable);

    const fieldValues: Record<string, any> = {
      ...response,
      open_hours: response.open_hours
        ? convertISOToDayJs(response.open_hours)
        : convertToDayJs(options.open_hours),
    };

    form.setFieldsValue(fieldValues);
  };

  const handleSuccess = () => {
    setReloadCount((prev: number) => prev + 1);
    closeFormAndReset();
    message.success(msg);
  };

  const closeFormAndReset = () => {
    setIsEditable(true);
    form.resetFields();
    onClose();
  };

  const Footer = () => {
    return (
      <div className={'footer'}>
        <Button
          label={intl.formatMessage({ id: 'general.cancel' })}
          onClick={closeFormAndReset}
        />
        <Button label={submitText} type={'primary'} onClick={handleSubmit} />
      </div>
    );
  };

  const initialValues = {
    open_hours: convertToDayJs(options.open_hours),
  };

  return (
    <StyledCreateEditInfrastructureDrawer
      footer={<Footer />}
      open={open}
      title={title}
      onClose={closeFormAndReset}
      width={800}
      closeIcon={<Icon name={'global-close-icon'} />}
    >
      <StyledCreateEditInfrastructureForm
        form={form}
        initialValues={initialValues}
      >
        <Spinner spinning={isInfrastructureLoading}>
          <Input name={'id'} hidden />
          <Input name={'farm_id'} hidden />
          <div className={'input-container'}>
            <Input
              label={intl.formatMessage({ id: 'general.pvd_number' })}
              layout={'vertical'}
              name={'pvd_number'}
              validations={['pvdNumber']}
              disabled={!isEditable}
            />

            <Input
              label={intl.formatMessage({ id: 'farms.object_name' })}
              layout={'vertical'}
              name={'object_name'}
              validations={['requiredText']}
              disabled={!isEditable}
            />

            <Input
              onClick={() => setIsOpen(true)}
              label={intl.formatMessage({ id: 'farms.address_pak' })}
              layout={'vertical'}
              name={'address_pak'}
              validations={['requiredText']}
              disabled={!isEditable}
              readOnly
            />

            <Input
              label={
                <div className={'infrastructures-maps-api-label'}>
                  <a className={'infrastructures-coordinates'}>
                    {intl.formatMessage({ id: 'farms.coordinates' })}
                  </a>
                  <a
                    className={'infrastructures-google-maps'}
                    href="https://www.google.com/maps"
                  >
                    {`(${intl.formatMessage({ id: 'farms.google_map' })})`}
                  </a>
                </div>
              }
              layout={'vertical'}
              name={'maps_api'}
              validations={['requiredText', 'mapsApi']}
            />

            <Input
              label={intl.formatMessage({ id: 'general.infrastructure' })}
              name={'structural_type'}
              layout={'vertical'}
            />

            <Checkbox
              name={'status'}
              label={intl.formatMessage({ id: 'general.status' })}
              initialValue={false}
            />

            <Checkbox
              name={'show_on_map'}
              label={intl.formatMessage({ id: 'farms.show_on_map' })}
              initialValue={false}
            />
          </div>

          <div className={'working-hours'}>
            <div className={'label-wrapper'}>
              <h4>{intl.formatMessage({ id: 'general.working_hours' })}</h4>
            </div>
            <WorkingHours />
          </div>

          <TextArea
            layout={'vertical'}
            name={'description'}
            label={intl.formatMessage({
              id: 'general.additional_info_description',
            })}
            className={'desc'}
          />
        </Spinner>
        <AdresSearch
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          onItemSelect={(event: AMKResponse) => {
            if (event.data?.code) {
              if (event.data?.irAdrese) {
                form.setFieldValue(
                  'address_pak',
                  `${event.data?.address}, ${event.data?.zipCode}`
                );

                setIsOpen(false);
              } else {
                alert(
                  intl.formatMessage({ id: 'general.warning_show_full_adress' })
                );
              }
            }
          }}
        />
      </StyledCreateEditInfrastructureForm>
    </StyledCreateEditInfrastructureDrawer>
  );
};
